import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type TicketCategory,
  type TicketMessageVisibility,
  type TicketPriority,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Notice } from '../ui/Badge';
import { CustomerSearchSelect } from '../ui/CustomerSearchSelect';
import { PressedGroup } from '../ui/PressedGroup';
import { CreateFooter } from './CreateFooter';
import { Field, field } from './Field';
import {
  categoryLabel,
  endOfDayInput,
  instantOfInput,
  mayOwnTickets,
  newTicketMissing,
  priorityLabel,
} from '../tickets/ticketView';
import { useTapOnce } from '../ui/useTapOnce';
import { CustomerEmailHint, useMayWriteToCustomer } from '../tickets/Messages';

/** How many of a customer's jobs the picker offers, newest first. */
export const JOB_PICKER_LIMIT = 50;

/** What a New ticket opens with: blank, or the job page's customer and job. */
export interface NewTicketStart {
  customerId?: string;
  jobId?: string;
}

/**
 * The office's "New ticket" (spec §5): a customer always, a job when there is one (G-T3, the
 * customer's own jobs only), a title, a category, a priority, an owner, a reply-due and a first
 * message. Urgent pre-fills the reply-due with "today, end of day" on the shop's clock (Q3);
 * nothing else does, and taking Urgent off again takes the pre-fill with it, unless it was
 * edited.
 */
export function NewTicketSheet({
  open,
  onClose,
  tz,
  start,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  tz: string;
  start?: NewTicketStart;
  /** The new ticket's id, once it has landed: the page opens it. */
  onCreated?: (ticketId: string) => void;
}) {
  // The form mounts with each opening (the sheet's content unmounts when it closes), so every
  // New ticket starts from `start`: the job page's customer and job, or blank.
  return (
    <Sheet open={open} onClose={onClose} title="New ticket">
      <NewTicketForm tz={tz} start={start} onClose={onClose} onCreated={onCreated} />
    </Sheet>
  );
}

/**
 * The form itself, its own component so a test can render it (a Radix portal never mounts in
 * the node harness, LESSONS 2026-10-05). Its action sits at its foot.
 */
export function NewTicketForm({
  tz,
  start,
  onClose,
  onCreated,
}: {
  tz: string;
  start?: NewTicketStart;
  onClose: () => void;
  onCreated?: (ticketId: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const reasonId = useId();
  const hintId = useId();
  const members = useQuery(trpc.org.members.queryOptions());
  const owners = (members.data ?? []).filter((m) => mayOwnTickets(m.role));

  const [customerId, setCustomerId] = useState(start?.customerId ?? '');
  const [jobId, setJobId] = useState(start?.jobId ?? '');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [ownerId, setOwnerId] = useState('');
  const [replyDue, setReplyDue] = useState('');
  /** The reply-due Urgent filled in, while nobody has changed it. */
  const [prefilled, setPrefilled] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [visibility, setVisibility] = useState<TicketMessageVisibility>('internal');
  const mayWriteToCustomer = useMayWriteToCustomer();
  const toCustomer = mayWriteToCustomer && visibility === 'customer';

  // `job.list` needs `job:browse`, which a tech never holds (CRM 006); this sheet is the
  // office's. Narrowing it to one customer here is a filter, not a scope.
  const jobs = useQuery({
    ...trpc.job.list.queryOptions({
      customerId: customerId || undefined,
      limit: JOB_PICKER_LIMIT,
      offset: 0,
      sort: 'created',
      dir: 'desc',
    }),
    enabled: customerId !== '',
  });

  const create = useMutation(
    trpc.ticket.create.mutationOptions({
      onSuccess: (ticket) => {
        void qc.invalidateQueries();
        onClose();
        onCreated?.(ticket.id);
      },
      onSettled: () => lock.release(),
    }),
  );

  const pickPriority = (next: TicketPriority) => {
    setPriority(next);
    if (next === 'urgent' && replyDue === '') {
      const eod = endOfDayInput(Date.now(), tz);
      setReplyDue(eod);
      setPrefilled(eod);
    } else if (next !== 'urgent' && prefilled !== null && replyDue === prefilled) {
      setReplyDue('');
      setPrefilled(null);
    }
  };

  const due = replyDue ? instantOfInput(replyDue, tz) : null;
  const missing = newTicketMissing({ customerId, title, category });

  return (
    <div className="space-y-3">
      {create.error ? (
        <Notice tone="danger" alert>
          {create.error.message}
        </Notice>
      ) : null}

      <CustomerSearchSelect
        value={customerId}
        onChange={(next) => {
          setCustomerId(next);
          setJobId('');
        }}
        autoFocus={!start?.customerId}
      />

      <Field label="Job (optional)">
        <select
          className={field}
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
          disabled={customerId === ''}
        >
          <option value="">No job</option>
          {(jobs.data?.rows ?? []).map((j) => (
            <option key={j.id} value={j.id}>
              #{j.jobNumber} {j.title}
            </option>
          ))}
          {/* The job it was opened on, before the list has loaded. */}
          {jobId && !jobs.data?.rows.some((j) => j.id === jobId) ? (
            <option value={jobId}>This job</option>
          ) : null}
        </select>
      </Field>
      {jobs.data && jobs.data.total > JOB_PICKER_LIMIT ? (
        <p className="text-xs text-muted-foreground">
          The {JOB_PICKER_LIMIT} newest of {jobs.data.total} jobs. Raise it from the job
          page for an older one.
        </p>
      ) : null}

      <Field label="Title">
        <input
          className={field}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Leak came back under the sink"
          autoFocus={Boolean(start?.customerId)}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Category">
          <select
            className={field}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Choose a category</option>
            {TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select
            className={field}
            value={priority}
            onChange={(e) => pickPriority(e.target.value as TicketPriority)}
          >
            {TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {priorityLabel(p)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <select
            className={field}
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">Nobody yet</option>
            {owners.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name ?? m.email}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reply due (optional)">
          <input
            className={field}
            type="datetime-local"
            value={replyDue}
            onChange={(e) => setReplyDue(e.target.value)}
          />
        </Field>
      </div>
      {prefilled !== null && replyDue === prefilled ? (
        <p className="text-xs text-muted-foreground">
          Urgent: due today, end of day. Change it if you promised another time.
        </p>
      ) : null}

      <Field label="First message (optional)">
        <textarea
          className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-describedby={message.trim() && toCustomer ? hintId : undefined}
          placeholder="What the customer said, what you promised"
        />
      </Field>
      {message.trim() && mayWriteToCustomer ? (
        <PressedGroup
          label="Who the message is for"
          options={[
            { value: 'internal', label: 'Internal' },
            { value: 'customer', label: 'For the customer' },
          ]}
          value={visibility}
          onChange={setVisibility}
        />
      ) : null}
      {message.trim() && toCustomer ? (
        <CustomerEmailHint id={hintId} linkToSettings={false} />
      ) : null}
      <div className="border-t border-border pt-4">
        <CreateFooter
          label="Create ticket"
          missing={missing}
          pending={create.isPending}
          reasonId={reasonId}
          onSubmit={() =>
            lock.run(() =>
              create.mutate({
                customerId,
                jobId: jobId || undefined,
                title: title.trim(),
                category: category as TicketCategory,
                priority,
                ownerId: ownerId || undefined,
                replyDueAt: due ?? undefined,
                message: message.trim()
                  ? {
                      body: message.trim(),
                      visibility: toCustomer ? 'customer' : 'internal',
                    }
                  : undefined,
                ...idem(),
              }),
            )
          }
        />
      </div>
    </div>
  );
}
